import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { usePlatoonStore } from '../store/platoonStore';
import { useAuthStore } from '../store/authStore';
import { useUnitStore } from '../store/unitStore';
import { PHASE_META, PLATOON_PHASES, type Platoon, type PlatoonPhase } from '../types/domain';

const OPERATOR_ROLES = ['COMMANDER', 'OPERATOR', 'UNIT_COMMANDER', 'UNIT_OPERATOR'];

// Doctrinal happy-path order (not enforced — any phase can follow any phase).
const NEXT_PHASE: Record<PlatoonPhase, PlatoonPhase> = {
  SECURITY: 'MOVING_OUT',
  MOVING_OUT: 'RECCE',
  RECCE: 'HIDEOUT',
  HIDEOUT: 'DRILLS',
  DRILLS: 'BRIEF',
  BRIEF: 'EXECUTING',
  EXECUTING: 'FALLBACK',
  FALLBACK: 'RECOVERED',
  RECOVERED: 'SECURITY',
};

export default function PhaseControl({ platoon }: { platoon: Platoon }) {
  const { user } = useAuthStore();
  const { isHQ } = useUnitStore();
  const setPhase = usePlatoonStore((s) => s.setPhase);
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // HQ commander/operator may set any platoon; a unit operator only their own unit.
  const canControl = useMemo(() => {
    if (!user || !OPERATOR_ROLES.includes(user.role)) return false;
    if (isHQ(user.unitId ?? null)) return true;
    return user.unitId === platoon.unitId;
  }, [user, isHQ, platoon.unitId]);

  const apply = async (phase: PlatoonPhase) => {
    if (busy) return;
    setBusy(true);
    try {
      await setPhase(platoon.id, phase, undefined, note.trim() || undefined);
      toast.success(`${platoon.callsign} → ${PHASE_META[phase].label}`);
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['platoons'] });
      queryClient.invalidateQueries({ queryKey: ['rotation-platoons'] });
      queryClient.invalidateQueries({ queryKey: ['operations'] });
    } catch {
      toast.error('Failed to set phase');
    } finally {
      setBusy(false);
    }
  };

  if (!canControl) {
    return (
      <div className="text-[11px] text-gray-500">Phase tagging is read-only for your role.</div>
    );
  }

  const next = NEXT_PHASE[platoon.currentPhase];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => apply(next)}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-command-accent/20 text-command-accent border border-command-accent/40 hover:bg-command-accent/30 disabled:opacity-50"
        >
          Advance → {PHASE_META[next].label}
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="px-2 py-1.5 rounded-lg text-xs text-gray-400 border border-command-border hover:border-command-accent/40"
        >
          {expanded ? 'Hide phases' : 'Set phase…'}
        </button>
      </div>

      {expanded && (
        <div className="space-y-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note (recorded on the AAR timeline)"
            className="w-full px-2 py-1.5 rounded-lg bg-command-bg border border-command-border text-xs text-gray-100 placeholder-gray-600 focus:outline-none focus:border-command-accent"
          />
          <div className="grid grid-cols-3 gap-1.5">
            {PLATOON_PHASES.map((phase) => {
              const meta = PHASE_META[phase];
              const isCurrent = phase === platoon.currentPhase;
              return (
                <button
                  key={phase}
                  type="button"
                  disabled={busy}
                  onClick={() => apply(phase)}
                  className={clsx(
                    'px-2 py-1.5 rounded-lg text-[11px] font-semibold border transition-transform hover:scale-[1.03] disabled:opacity-50',
                    isCurrent && 'ring-2 ring-offset-1 ring-offset-command-bg'
                  )}
                  style={{
                    color: meta.color,
                    backgroundColor: `${meta.color}1a`,
                    borderColor: `${meta.color}55`,
                  }}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
